import React, { PropsWithChildren, useContext, useEffect, useRef } from 'react'
import { Button, ButtonModel, IconRegularCalendar, IconRegularCheckmark, IconSolidStar } from '@notino/react-styleguide'
import { useIntl } from 'react-intl'
import { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react'

// hooks
import useMessages from '../../../hooks/useMessages'

// styles
import * as SC from './Step4SummarytStyles'
import { BREAKPOINTS } from '../../../styles/constants'

// components
import RenderPrice from '../../RenderPrice/RenderPrice'

// assets
import ClockIcon from '../../../assets/icons/ClockIcon'
import CloseIcon from '../../../assets/icons/CloseIcon'
import ScissorsIcon from '../../../assets/icons/ScissorsIcon'

// types
import { CmsThankYouPageBannerData, PostCalendarEventsReservationResponse, ReservationBookingModalCloseType, SalonServiceResponse } from '../../../types/types'

// utils
import { getModalNextReservationEvent, getModalReservationPurchaseEvent, getPromotionEvent } from '../../../utils/dataLayerEvents'
import { pushToDataLayer } from '../../../utils/dataLayer'
import { PAGE_LINKS } from '../../../utils/helper'
import { MyLocationContext } from '../../../utils/myLocationProvider'

type Props = PropsWithChildren<{
	onClose: (closeType: ReservationBookingModalCloseType) => void
	onRedirectToHomepage: () => void
	submittedReservationData: PostCalendarEventsReservationResponse['reservationCalendarEvent']
	selectedServiceData: SalonServiceResponse['service']
	cmsData: CmsThankYouPageBannerData
}>

const Step4Summary = (props: Props) => {
	const { onClose, onRedirectToHomepage, submittedReservationData, selectedServiceData, cmsData } = props
	const { image: promoBanner } = cmsData

	const { messages } = useMessages()
	const { formatDate, locale } = useIntl()
	const { myLocation } = useContext(MyLocationContext)

	const { salon, service, noteFromB2CCustomer, employee, customer, visitor, start, end, reservationData, visitorSameAsCustomer } = submittedReservationData
	const salonCoverImageUrl = salon.images?.find((image) => image.isCover)?.resizedImages.thumbnail

	const isPurchaseDataLayerEventSend = useRef(false)
	const isPromoBannerViewDataLayerEventSend = useRef(false)

	const scrollableContentRef = useRef<OverlayScrollbarsComponentRef<React.ElementType>>(null)
	const promoBannerRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (isPurchaseDataLayerEventSend.current) {
			return
		}
		pushToDataLayer(
			getModalReservationPurchaseEvent({
				postReservationData: submittedReservationData,
				industryID: selectedServiceData.category.id,
				secondLevelCategoryID: selectedServiceData.category.child.id,
				serviceCategoryID: selectedServiceData.category.child.child?.id ?? ''
			})
		)
		isPurchaseDataLayerEventSend.current = true
	}, [submittedReservationData, selectedServiceData.category.id, selectedServiceData.category.child.id, selectedServiceData.category.child.child?.id])

	// Tracks promo banner view event using IntersectionObserver. Sends the event once when the banner is 50% visible.
	useEffect(() => {
		if (!promoBannerRef.current || !scrollableContentRef.current) {
			return undefined
		}

		const osInstance = scrollableContentRef.current.osInstance?.()
		const rootElement = osInstance?.elements().viewport

		if (!rootElement) {
			return undefined
		}

		const observer = new IntersectionObserver(
			([entry]) => {
				if (entry.intersectionRatio >= 0.5 && !isPromoBannerViewDataLayerEventSend.current) {
					pushToDataLayer(
						getPromotionEvent('view_promotion', [
							{ code: promoBanner.trackingID, location: 'booking_type', position: 1, destination_url: promoBanner.link || 'unclickable' }
						])
					)
					isPromoBannerViewDataLayerEventSend.current = true
					observer.disconnect()
				}
			},
			{
				root: rootElement,
				threshold: [0.5]
			}
		)

		observer.observe(promoBannerRef.current)

		return () => observer.disconnect()
	}, [promoBanner.trackingID, promoBanner.link])

	const footerButtons = (
		<>
			<Button
				onClick={() => {
					pushToDataLayer(getModalNextReservationEvent())
					onClose(null)
				}}
				fullWidth
				buttonStyle={ButtonModel.Styles.secondary}
			>
				{messages['Next reservation']}
			</Button>
			<SC.LinkButton
				href={PAGE_LINKS['/salons/homepage'](locale, { ...myLocation })}
				onClick={onRedirectToHomepage}
				fullWidth
				buttonStyle={ButtonModel.Styles.primary}
			>
				{messages['Close booking modal']}
			</SC.LinkButton>
		</>
	)

	const promoBannerPicture = (
		<SC.Picture
			onClick={() => {
				// NOTE from Petr Haken: select_promotion prosim poslat, pokud se uzivatel snazi nekterou z tech ploch prokliknout (at uz to jde nebo nejde)
				pushToDataLayer(
					getPromotionEvent('select_promotion', [
						{
							code: promoBanner.trackingID,
							location: 'booking_type',
							position: 1,
							destination_url: promoBanner.link || 'unclickable'
						}
					])
				)
			}}
		>
			<source srcSet={promoBanner.desktop} media={`(min-width: ${BREAKPOINTS.lg})`} />
			<source srcSet={promoBanner.mobile} />
			<img src={promoBanner.desktop} alt={promoBanner.alt} />
		</SC.Picture>
	)

	return (
		<SC.SummaryLayoutWrapper>
			<SC.ScrollableContent ref={scrollableContentRef}>
				<SC.SummaryWrapper>
					<SC.SummaryGrid>
						<SC.CloseIconWrapper onClick={() => onClose('button_x')}>
							<CloseIcon />
						</SC.CloseIconWrapper>
						<SC.PromoBanner ref={promoBannerRef}>
							<SC.PromoBannerContent>
								{promoBanner.link ? (
									<SC.PromoBannerLink href={promoBanner.link} target={'blank'} rel={'noopener noreferrer'}>
										{promoBannerPicture}
									</SC.PromoBannerLink>
								) : (
									promoBannerPicture
								)}
							</SC.PromoBannerContent>
						</SC.PromoBanner>
						<SC.ReservationContent>
							<div>
								<SC.ReservationTitleWrapper>
									<SC.CheckIcon>
										<IconRegularCheckmark color={'icon.inverse'} width={'2.375rem'} height={'2.375rem'} />
									</SC.CheckIcon>
									<SC.ReservationTitle>{messages['The reservation has been successfully created']}</SC.ReservationTitle>
								</SC.ReservationTitleWrapper>
								<SC.ReservationWrapper>
									{salon?.logo && (
										<SC.SalonLogo>
											<img src={salon.logo?.resizedImages.thumbnail} alt='salon logo' />
										</SC.SalonLogo>
									)}
									<SC.SalonInfo>
										{salonCoverImageUrl && (
											<SC.SalonCover>
												<img loading='lazy' src={salonCoverImageUrl} alt='salon cover' />
											</SC.SalonCover>
										)}
										<div>
											{salon?.rating && (
												<SC.Rating>
													<IconSolidStar color={'icon.highlight'} width='12' height='12' style={{ marginBottom: '2px' }} />
													<span>{salon.rating.toFixed(1)}</span>
												</SC.Rating>
											)}
											{salon?.name && <SC.SalonTitle>{salon.name}</SC.SalonTitle>}
											<div>
												{salon?.address && <SC.LineWithIcon>{salon.formattedAddress}</SC.LineWithIcon>}
												{salon?.openingHoursData && (
													<SC.OpeningHours $isOpen={salon.openingHoursData?.isOpen}>
														<ClockIcon />
														<span>
															<SC.OpeningHoursStatus $isOpen={salon.openingHoursData?.isOpen}>
																{salon.openingHoursData.isOpen ? messages.Open : messages.Close}
															</SC.OpeningHoursStatus>
															{salon.openingHoursData.message && ` • ${salon.openingHoursData.message}`}
														</span>
													</SC.OpeningHours>
												)}
											</div>
										</div>
									</SC.SalonInfo>
									<SC.Divider />
									<SC.Service>
										<SC.ServiceValues>
											<SC.ServiceValue>
												<ScissorsIcon />
												<SC.LabelRegular>{service?.name}</SC.LabelRegular>
											</SC.ServiceValue>
											<SC.ServiceValue>
												<IconRegularCalendar />
												<SC.LabelRegular>
													{formatDate(start.date, { dateStyle: 'medium' })} {start.time} - {end.time}
												</SC.LabelRegular>
											</SC.ServiceValue>
											<SC.EmployeeWrapper>
												{employee.image.resizedImages.thumbnail && <SC.EmployeeAvatar src={employee.image.resizedImages.thumbnail} />}
												<SC.LabelRegular>
													{employee.firstName || employee.lastName ? (
														<>
															{employee.firstName} {employee.lastName}
														</>
													) : (
														employee.email
													)}
												</SC.LabelRegular>
											</SC.EmployeeWrapper>
										</SC.ServiceValues>
										<SC.Price>
											<RenderPrice from={reservationData?.priceFrom} to={reservationData?.priceTo} />
										</SC.Price>
									</SC.Service>
									{noteFromB2CCustomer && <SC.Note>{noteFromB2CCustomer}</SC.Note>}
									<SC.Divider />
									<SC.CustomerWrapper>
										{customer && (
											<SC.Customer>
												<SC.CustomerLabel>{messages.Customer}</SC.CustomerLabel>
												<SC.CustomerValue>
													<SC.LabelRegular>
														{customer.firstName} {customer.lastName}
													</SC.LabelRegular>
													<SC.CustomerEmail>{customer.email}</SC.CustomerEmail>
													<SC.CustomerPhone>
														{customer.phonePrefix} {customer.phone}
													</SC.CustomerPhone>
												</SC.CustomerValue>
											</SC.Customer>
										)}
										{!visitorSameAsCustomer && visitor && (
											<SC.Customer>
												<SC.CustomerLabel>{messages.Visitor}</SC.CustomerLabel>
												<SC.LabelRegular>
													{visitor.firstName} {visitor.lastName}
												</SC.LabelRegular>
											</SC.Customer>
										)}
									</SC.CustomerWrapper>
								</SC.ReservationWrapper>
							</div>
							<SC.Footer>{footerButtons}</SC.Footer>
						</SC.ReservationContent>
					</SC.SummaryGrid>
				</SC.SummaryWrapper>
			</SC.ScrollableContent>
			<SC.MobileFooter>{footerButtons}</SC.MobileFooter>
		</SC.SummaryLayoutWrapper>
	)
}

export default Step4Summary
